import { useState } from 'react'
import { digitsOnly, formatCardNumber, formatExpiryInput, getCardMeta, cardLast4 } from './creditCard'

export default function CreditCardInput ({ ccnumber = '', ccexp = '', cvv = '', onChange }) {
  const [touched, setTouched] = useState({ number: false, expiry: false, cvv: false })
  const meta = getCardMeta({ ccnumber, ccexp, cvv })

  const setNumber = (value) => {
    const ccnumber = digitsOnly(formatCardNumber(value))
    onChange({ ccnumber, ccexp, cvv, last4: cardLast4(ccnumber) })
  }
  const setExpiry = (value) => onChange({ ccnumber, ccexp: digitsOnly(formatExpiryInput(value)), cvv, last4: cardLast4(ccnumber) })
  const setCvv = (value) => onChange({ ccnumber, ccexp, cvv: digitsOnly(value).slice(0, meta.cvvLength), last4: cardLast4(ccnumber) })

  const numberError = ccnumber && !meta.numberValid && (touched.number || meta.numberComplete || !meta.numberPotentiallyValid)
    ? 'Enter a valid card number'
    : ''
  const expiryError = ccexp && !meta.expiryValid && (touched.expiry || meta.expiryComplete || !meta.expiryPotentiallyValid)
    ? 'Enter a valid expiration (MM/YY)'
    : ''
  const cvvError = cvv && !meta.cvvValid && (touched.cvv || meta.cvvComplete || !meta.cvvPotentiallyValid)
    ? `Enter a valid ${meta.cvvLength}-digit CVV`
    : ''

  return (
    <fieldset className='flex flex-col gap-2'>
      <legend className='text-xs font-semibold text-slate-500 dark:text-slate-400'>Card information</legend>
      <div className='flex flex-col sm:flex-row min-h-11 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden focus-within:ring-2 focus-within:ring-sky-500'>
        <label className='flex-1 min-w-0 px-3 py-2'>
          <span className='sr-only'>Card number</span>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='cc-number'
            placeholder='Card number'
            aria-invalid={Boolean(numberError)}
            aria-describedby={numberError ? 'card-number-error' : undefined}
            className='w-full bg-transparent text-sm text-slate-900 dark:text-white outline-none py-1'
            value={meta.formattedNumber}
            onChange={(e) => setNumber(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, number: true }))}
          />
        </label>
        <label className='sm:w-28 px-3 py-2 sm:border-l border-t sm:border-t-0 border-gray-200 dark:border-slate-600'>
          <span className='sr-only'>Expiration date</span>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='cc-exp'
            placeholder='MM / YY'
            aria-invalid={Boolean(expiryError)}
            aria-describedby={expiryError ? 'card-expiry-error' : undefined}
            className='w-full bg-transparent text-sm text-slate-900 dark:text-white outline-none py-1'
            value={meta.formattedExpiry}
            onChange={(e) => setExpiry(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, expiry: true }))}
          />
        </label>
        <label className='sm:w-24 px-3 py-2 sm:border-l border-t sm:border-t-0 border-gray-200 dark:border-slate-600'>
          <span className='sr-only'>CVV</span>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='cc-csc'
            placeholder='CVV'
            aria-invalid={Boolean(cvvError)}
            aria-describedby={cvvError ? 'card-cvv-error' : undefined}
            className='w-full bg-transparent text-sm text-slate-900 dark:text-white outline-none py-1'
            value={cvv}
            onChange={(e) => setCvv(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, cvv: true }))}
          />
        </label>
        {meta.cardType ? (
          <p className='hidden sm:flex items-center px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 whitespace-nowrap'>
            {meta.cardType}
          </p>
        ) : null}
      </div>
      <div className='text-xs text-rose-600 space-y-1' aria-live='polite'>
        {numberError ? <p id='card-number-error'>{numberError}</p> : null}
        {expiryError ? <p id='card-expiry-error'>{expiryError}</p> : null}
        {cvvError ? <p id='card-cvv-error'>{cvvError}</p> : null}
      </div>
    </fieldset>
  )
}
